import { capitalize } from '@attain-apps/string-utils'
import { TypeChecker } from '@attain-apps/type-guards'

import * as interfaces from './interfaces'
import * as value from './value'

type AttributeBase<TId extends Attribute.Id = Attribute.Id> =
  interfaces.Identified<TId> &
    interfaces.Named &
    interfaces.Described.Optionally &
    interfaces.Hideable
/**
 * Represents metadata about a property, field, or column of a data structure or
 * table
 *
 * __NOTE:__ not exactly the same as a DMS attribute
 */
export type Attribute<TId extends Attribute.Id = Attribute.Id> =
  AttributeBase<TId> & {
    /** @see {@link value.Info} */
    valueInfo?: value.Info
  }
export namespace Attribute {
  export const isAttribute: TypeChecker<Attribute> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.intersection(
        interfaces.Identified.getTypeChecker(Id.isId),
        interfaces.Named.isNamed,
        interfaces.Described.Optionally.isOptionally,
        interfaces.Hideable.isHideable,
        TypeChecker.Factory.object<Difference<Attribute, AttributeBase>>({
          valueInfo: value.Info.isInfo,
        }),
      ),
  ).usingAlias('common.Attribute')

  /**
   * Identifies an {@link Attribute}
   */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.isString
  }

  /**
   * Typically, there is some way to get the value of an {@link Attribute} from
   * a row or item. In some cases, it is as simple as:
   * ```ts
   * const value = row[attribute.id]
   * ```
   * In other cases, it is more complicated. This type can be used to flexibly
   * handle this task.
   *
   * @example
   * type AttributeWithValueExtractor<TRow> =
   *   ents.core.common.Attribute
   *    & ents.core.common.Attribute.ValueExtractor<TRow>
   *
   * function logValuesOf<TRow>(
   *   row: TRow,
   *   attributes: readonly AttributeWithValueExtractor<TRow>[]
   * ): void {
   *   for (const attribute of attributes) {
   *     const value = attribute.getValueFrom(row)
   *     console.log(`${attribute.name}: ${value}`)
   *   }
   * }
   *
   * const exampleRow = { firstName: 'John', lastName: 'Doe', age: 45 }
   * const attributes: readonly AttributeWithValueExtractor<typeof exampleRow>[] = [
   *   { id: 'name', getValueFrom: row => `${row.firstName} ${row.lastName}`, ... },
   *   { id: 'age', getValueFrom: row => row.age, ... },
   * ]
   * logValuesOf(exampleRow, attributes)
   */
  export interface ValueExtractor<TSource, TVal = unknown> {
    getValueFrom(item: TSource): TVal
  }

  const isListOfObjects = TypeChecker.Factory.list(TypeChecker.isObject)
  /**
   * Infers attributes from the provided object or objects.
   */
  export const inferFrom = (
    itemOrItems: Maybe.ReadonlyArray<object>,
    { skipKeys = [] }: Readonly<inferFrom.Options> = {},
  ): Attribute[] => {
    if (isListOfObjects(itemOrItems)) {
      const visitedIds = new Set<string>()
      return itemOrItems
        .flatMap(item => inferFrom(item, { skipKeys }))
        .filter(({ id }) => {
          if (visitedIds.has(id)) {
            return false
          }
          visitedIds.add(id)
          return true
        })
    }
    return Object.keys(itemOrItems)
      .filter(key => (skipKeys ? !skipKeys.includes(key) : false))
      .map(id => ({ id, name: capitalize(id) }))
  }
  export namespace inferFrom {
    export interface Options {
      /**
       * Skip keys, from the object
       *
       * @default []
       */
      skipKeys?: readonly string[]
    }
  }
}
